import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useState } from "react";

const NON_TYPING_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

// Taller than a browser toolbar collapsing, shorter than any phone keyboard.
const MIN_KEYBOARD_HEIGHT = 150;

type FocusTarget = {
  tagName: string;
  type?: string;
  readOnly?: boolean;
  isContentEditable?: boolean;
};

export function opensSoftKeyboard(target: FocusTarget | null): boolean {
  if (target == null) return false;
  if (target.isContentEditable) return true;

  const tag = target.tagName.toUpperCase();
  if (tag === "TEXTAREA") return !target.readOnly;
  if (tag !== "INPUT") return false;

  return (
    !target.readOnly &&
    !NON_TYPING_INPUT_TYPES.has((target.type ?? "text").toLowerCase())
  );
}

export function isShrunkByKeyboard(
  fullHeight: number,
  currentHeight: number,
): boolean {
  return fullHeight - currentHeight >= MIN_KEYBOARD_HEIGHT;
}

function focusedElementOpensKeyboard(): boolean {
  const active = document.activeElement;
  return active instanceof HTMLElement && opensSoftKeyboard(active);
}

/**
 * True on touch-sized screens while a text field has focus and the visual
 * viewport has shrunk by a keyboard's height. Focus alone is not enough: modal
 * focus traps re-focus the field after iOS "Done" closes the keyboard.
 */
export function useSoftKeyboardOpen(): boolean {
  const isTouchScreen =
    useMediaQuery("(pointer: coarse) and (max-width: 768px)") ?? false;
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isTouchScreen) return;

    const viewport = window.visualViewport;
    const full = {
      width: viewport?.width ?? 0,
      height: viewport?.height ?? 0,
    };
    const reported: { isOpen: boolean | null } = { isOpen: null };

    function isKeyboardOpen(): boolean {
      if (viewport == null) return focusedElementOpensKeyboard();

      if (viewport.width !== full.width) {
        full.width = viewport.width;
        full.height = viewport.height;
      } else {
        full.height = Math.max(full.height, viewport.height);
      }

      return (
        focusedElementOpensKeyboard() &&
        isShrunkByKeyboard(full.height, viewport.height)
      );
    }

    // Chromium fires focusout inside React's commit when a focused node is
    // removed; an unchanged setState there still re-renders and can loop.
    function update() {
      const next = isKeyboardOpen();
      if (next === reported.isOpen) return;
      reported.isOpen = next;
      setIsOpen(next);
    }

    update();
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", update);
    viewport?.addEventListener("resize", update);
    return () => {
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", update);
      viewport?.removeEventListener("resize", update);
    };
  }, [isTouchScreen]);

  return isTouchScreen && isOpen;
}
